import './observability'
import { useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import { queryClient } from '@/api/query-client'
import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from '@/components/ui/toaster'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AuthProvider, useAuth } from '@/components/auth'
import { ChatContextProvider } from '@/components/chat/chat-context'
import './index.css'

// Create the router instance with initial context
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {
    auth: {
      user: null,
      isLoading: true,
    },
  },
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Root element not found')
}

const root = createRoot(rootElement)
root.render(
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <ChatContextProvider>
          <InnerApp />
          <Toaster />
        </ChatContextProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>,
)

// Component to provide router context with auth state
function InnerApp() {
  const { user, isLoading } = useAuth()

  // Update router context when auth state changes
  useEffect(() => {
    router.invalidate()
  }, [user, isLoading])

  return (
    <RouterProvider
      router={router}
      context={{
        auth: {
          user: user ? { id: user.id, email: user.email, username: user.username ?? null } : null,
          isLoading,
        },
      }}
    />
  )
}
